import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Table,
  Button,
  Input,
  Form,
  message,
  Spin,
  Tabs,
  Progress,
} from 'antd';
import {
  ThunderboltOutlined,
  SunOutlined,
  CloudOutlined,
  FireOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';

const { Title } = Typography;
const { TabPane } = Tabs;

interface EnergyBreakdown {
  solar: number;
  wind: number;
  naturalGas: number;
  grid: number;
  battery: number;
  total: number;
}

interface EmissionsData {
  daily: Array<{
    timestamp: string;
    totalEmissions: number;
    renewableOffset: number;
    netEmissions: number;
  }>;
  totals: {
    totalEmissionsTons: number;
    renewableOffsetTons: number;
    netEmissionsTons: number;
  };
}

const EnergyDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [breakdown, setBreakdown] = useState<EnergyBreakdown | null>(null);
  const [emissions, setEmissions] = useState<EmissionsData | null>(null);
  const [optimization, setOptimization] = useState<any>(null);
  const [roiData, setRoiData] = useState<any>(null);

  useEffect(() => {
    if (user) {
      fetchEnergyData();
    }
  }, [user]);

  const fetchEnergyData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const [breakdownRes, emissionsRes, optimizationRes] = await Promise.all([
        axios.get(`${import.meta.env.VITE_API_URL}/api/energy/breakdown`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${import.meta.env.VITE_API_URL}/api/energy/emissions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${import.meta.env.VITE_API_URL}/api/energy/optimization`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setBreakdown(breakdownRes.data.data.averageBreakdown);
      setEmissions(emissionsRes.data.data);
      setOptimization(optimizationRes.data.data);
    } catch (error) {
      console.error('Error fetching energy data:', error);
      message.error('Failed to load energy data');
    } finally {
      setLoading(false);
    }
  };

  const handleNaturalGasSubmit = async (values: any) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/energy/natural-gas`,
        {
          naturalGasConsumption: values.consumption,
          naturalGasCost: values.cost,
          unit: values.unit || 'therms',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      message.success('Natural gas data recorded successfully');
      fetchEnergyData();
    } catch (error) {
      message.error('Failed to record natural gas data');
    } finally {
      setLoading(false);
    }
  };

  const handleROICalculate = async (values: any) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/energy/roi`,
        {
          params: {
            systemType: values.systemType,
            systemSize: values.systemSize,
            installationCost: values.installationCost,
            currentEnergyCost: values.currentEnergyCost,
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setRoiData(response.data.data);
      message.success('ROI calculated successfully');
    } catch (error) {
      message.error('Failed to calculate ROI');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !breakdown) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Energy Intelligence Dashboard</Title>

      <Tabs defaultActiveKey="overview">
        <TabPane tab="Overview" key="overview">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Solar Output"
                  value={breakdown?.solar.toFixed(1) || 0}
                  suffix="%"
                  prefix={<SunOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Wind Output"
                  value={breakdown?.wind.toFixed(1) || 0}
                  suffix="%"
                  prefix={<CloudOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Natural Gas"
                  value={breakdown?.naturalGas.toFixed(1) || 0}
                  suffix="%"
                  prefix={<FireOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Grid"
                  value={breakdown?.grid.toFixed(1) || 0}
                  suffix="%"
                  prefix={<ThunderboltOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {breakdown && (
            <Card title="Energy Source Breakdown" style={{ marginTop: 16 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Progress
                    type="circle"
                    percent={breakdown.solar}
                    format={(percent) => `${percent?.toFixed(1)}%`}
                  />
                  <div style={{ textAlign: 'center', marginTop: 8 }}>Solar</div>
                </Col>
                <Col span={6}>
                  <Progress
                    type="circle"
                    percent={breakdown.wind}
                    format={(percent) => `${percent?.toFixed(1)}%`}
                  />
                  <div style={{ textAlign: 'center', marginTop: 8 }}>Wind</div>
                </Col>
                <Col span={6}>
                  <Progress
                    type="circle"
                    percent={breakdown.naturalGas}
                    format={(percent) => `${percent?.toFixed(1)}%`}
                  />
                  <div style={{ textAlign: 'center', marginTop: 8 }}>Natural Gas</div>
                </Col>
                <Col span={6}>
                  <Progress
                    type="circle"
                    percent={breakdown.grid}
                    format={(percent) => `${percent?.toFixed(1)}%`}
                  />
                  <div style={{ textAlign: 'center', marginTop: 8 }}>Grid</div>
                </Col>
              </Row>
            </Card>
          )}
        </TabPane>

        <TabPane tab="Emissions" key="emissions">
          {emissions && (
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Total Emissions"
                    value={emissions.totals.totalEmissionsTons.toFixed(2)}
                    suffix="tons CO₂"
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Renewable Offset"
                    value={emissions.totals.renewableOffsetTons.toFixed(2)}
                    suffix="tons CO₂"
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Net Emissions"
                    value={emissions.totals.netEmissionsTons.toFixed(2)}
                    suffix="tons CO₂"
                    valueStyle={{
                      color:
                        emissions.totals.netEmissionsTons > 0
                          ? '#cf1322'
                          : '#3f8600',
                    }}
                  />
                </Card>
              </Col>
            </Row>
          )}
        </TabPane>

        <TabPane tab="Natural Gas" key="natural-gas">
          <Card title="Record Natural Gas Consumption">
            <Form onFinish={handleNaturalGasSubmit} layout="vertical">
              <Form.Item
                name="consumption"
                label="Consumption"
                rules={[{ required: true, message: 'Please enter consumption' }]}
              >
                <Input type="number" placeholder="Enter consumption" />
              </Form.Item>
              <Form.Item name="unit" label="Unit" initialValue="therms">
                <Input placeholder="therms, cubic_feet, or mbtu" />
              </Form.Item>
              <Form.Item name="cost" label="Cost ($)">
                <Input type="number" placeholder="Enter cost per unit" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Record Natural Gas
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab="ROI Calculator" key="roi">
          <Card title="Calculate ROI for Renewable Energy">
            <Form onFinish={handleROICalculate} layout="vertical">
              <Form.Item
                name="systemType"
                label="System Type"
                rules={[{ required: true }]}
              >
                <Input placeholder="solar or wind" />
              </Form.Item>
              <Form.Item name="systemSize" label="System Size (kW)">
                <Input type="number" placeholder="e.g., 10" />
              </Form.Item>
              <Form.Item name="installationCost" label="Installation Cost ($)">
                <Input type="number" placeholder="e.g., 30000" />
              </Form.Item>
              <Form.Item name="currentEnergyCost" label="Current Energy Cost ($/kWh)">
                <Input type="number" placeholder="e.g., 0.12" step="0.01" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Calculate ROI
                </Button>
              </Form.Item>
            </Form>

            {roiData && (
              <Card title="ROI Results" style={{ marginTop: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Payback Period"
                      value={roiData.paybackPeriod.toFixed(1)}
                      suffix="years"
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="25-Year ROI"
                      value={roiData.roi25Year.toFixed(1)}
                      suffix="%"
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Total Savings (25 years)"
                      value={roiData.totalSavings25Year.toFixed(2)}
                      prefix="$"
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="CO₂ Offset"
                      value={roiData.environmentalImpact.co2Offset.toFixed(2)}
                      suffix="tons"
                    />
                  </Col>
                </Row>
              </Card>
            )}
          </Card>
        </TabPane>

        <TabPane tab="Optimization" key="optimization">
          {optimization && (
            <Card title="Energy Optimization Recommendations">
              <ul>
                {optimization.recommendations.map((rec: string, index: number) => (
                  <li key={index} style={{ marginBottom: 8 }}>
                    {rec}
                  </li>
                ))}
              </ul>
              {optimization.potentialSavings > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Statistic
                    title="Potential Monthly Savings"
                    value={optimization.potentialSavings.toFixed(2)}
                    prefix="$"
                  />
                </div>
              )}
            </Card>
          )}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default EnergyDashboard;

